/**
 * Resolves the base URL of the OEM tray prediction microservice (`POST /predict`).
 *
 * Priority: runtime config (`window.__RUNTIME_CONFIG__`) → build-time env → localhost
 * default. Set `VITE_OEM_TRAY_API_URL` to point at a different host; set it to an empty
 * string to disable the real API (callers then fall back to a mock).
 */

interface RuntimeConfig {
  VITE_OEM_TRAY_API_URL?: string;
}

interface WindowWithRuntimeConfig {
  __RUNTIME_CONFIG__?: RuntimeConfig;
}

interface GlobalWithImportMetaEnv {
  import?: {
    meta?: {
      env?: RuntimeConfig;
    };
  };
}

/** Default local-dev address of the OEM microservice. */
export const DEFAULT_OEM_TRAY_API_URL = 'http://localhost:8000';

/**
 * `null` means the real API is explicitly disabled (env set to an empty string) —
 * callers should fall back to the mock. A non-empty string is the base URL to POST
 * `/predict` to.
 */
export const getOemTrayApiUrl = (): string | null => {
  if (typeof window !== 'undefined') {
    const runtimeValue = (window as WindowWithRuntimeConfig).__RUNTIME_CONFIG__
      ?.VITE_OEM_TRAY_API_URL;
    if (
      runtimeValue !== undefined &&
      runtimeValue !== '${VITE_OEM_TRAY_API_URL}'
    ) {
      const trimmed = runtimeValue.trim();
      return trimmed === '' ? null : trimmed;
    }
  }

  // Read build-time env without `import.meta` syntax so Jest can compile this file.
  const viteValue = (globalThis as GlobalWithImportMetaEnv).import?.meta?.env
    ?.VITE_OEM_TRAY_API_URL;
  if (typeof viteValue === 'string') {
    const trimmed = viteValue.trim();
    return trimmed === '' ? null : trimmed;
  }

  if (typeof process !== 'undefined') {
    const processValue = process.env?.['VITE_OEM_TRAY_API_URL'];
    if (typeof processValue === 'string') {
      const trimmed = processValue.trim();
      return trimmed === '' ? null : trimmed;
    }
  }

  return DEFAULT_OEM_TRAY_API_URL;
};
